import React, { useEffect, useRef, useState } from 'react';

/**
 * Heuristic resolver for picking a renderer. Prefer the MIME type when the
 * browser supplied one; fall back to the filename extension. We accept both
 * the canonical OOXML mimes ("…wordprocessingml.document") and the historical
 * short forms ("application/msword" for legacy .doc).
 */
type ResolvedKind = 'pdf' | 'image' | 'docx' | 'xlsx' | 'csv' | 'text' | 'rtf' | 'unsupported';

const IMG_EXT = new Set(['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg']);

function resolveKind(mime?: string | null, filename?: string | null): ResolvedKind {
  const m = (mime || '').toLowerCase();
  const ext = (() => {
    if (!filename) return '';
    const dot = filename.lastIndexOf('.');
    return dot >= 0 ? filename.slice(dot + 1).toLowerCase() : '';
  })();

  if (m.includes('pdf') || ext === 'pdf') return 'pdf';
  if (m.startsWith('image/') || IMG_EXT.has(ext)) return 'image';
  if (m.includes('wordprocessingml') || ext === 'docx' || ext === 'doc' || m === 'application/msword') return 'docx';
  if (m.includes('spreadsheetml') || ext === 'xlsx' || ext === 'xls' || m === 'application/vnd.ms-excel') return 'xlsx';
  if (m === 'text/csv' || ext === 'csv') return 'csv';
  if (m === 'application/rtf' || m === 'text/rtf' || ext === 'rtf') return 'rtf';
  if (m.startsWith('text/') || ext === 'txt' || ext === 'md' || ext === 'log') return 'text';
  return 'unsupported';
}

/**
 * Lazily-loaded pdf.js (legacy build) singleton. Reused across mounts to
 * avoid re-importing the ~1 MB worker bundle on every preview open. We
 * point `GlobalWorkerOptions.workerSrc` at the asset URL CRA emits for the
 * legacy worker — same pattern already used by `localDocumentProcessing.ts`.
 */
let pdfjsLibPromise: Promise<any> | null = null;
async function getPdfJsLib(): Promise<any> {
  if (!pdfjsLibPromise) {
    pdfjsLibPromise = import('pdfjs-dist/legacy/build/pdf.mjs' as any).then((m: any) => {
      try {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any, global-require
        (m as any).GlobalWorkerOptions.workerSrc = require('pdfjs-dist/legacy/build/pdf.worker.min.mjs');
      } catch {
        // If the bundler can't resolve the worker, pdf.js falls back to its
        // own defaults; rendering may be slower but still functional.
      }
      return m;
    });
  }
  return pdfjsLibPromise;
}

interface PdfCanvasViewerProps {
  url: string;
  blob: Blob | null | undefined;
  filename?: string | null;
  maxHeight: string;
}

const ZOOM_MIN = 0.5;
const ZOOM_MAX = 4.0;
const ZOOM_STEP = 0.25;

const clampZoom = (z: number): number => {
  const c = Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, z));
  // Round to two decimals so 0.5 / 0.75 / 1.0 / 1.25 … snap cleanly and we
  // never accumulate floating-point cruft from repeated +0.25 / -0.25 steps.
  return Math.round(c * 100) / 100;
};

const IconZoomOut: React.FC = () => (
  <svg
    viewBox="0 0 24 24"
    width="18"
    height="18"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <circle cx="11" cy="11" r="7" />
    <line x1="20" y1="20" x2="16.65" y2="16.65" />
    <line x1="8" y1="11" x2="14" y2="11" />
  </svg>
);

const IconZoomIn: React.FC = () => (
  <svg
    viewBox="0 0 24 24"
    width="18"
    height="18"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <circle cx="11" cy="11" r="7" />
    <line x1="20" y1="20" x2="16.65" y2="16.65" />
    <line x1="8" y1="11" x2="14" y2="11" />
    <line x1="11" y1="8" x2="11" y2="14" />
  </svg>
);

const IconFitWidth: React.FC = () => (
  <svg
    viewBox="0 0 24 24"
    width="18"
    height="18"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <line x1="4" y1="12" x2="20" y2="12" />
    <polyline points="8 8 4 12 8 16" />
    <polyline points="16 8 20 12 16 16" />
  </svg>
);

interface ZoomControlClusterProps {
  zoom: number;
  onZoomIn: () => void;
  onZoomOut: () => void;
  onFitWidth: () => void;
  /** Localised group label — different per format (PDF vs document vs sheet)
   *  so a screen reader announces the right thing. */
  ariaLabel: string;
  /** Marker class added to the cluster wrapper for per-format positioning
   *  overrides. The visual rules live on the shared `.doc-preview-zoom`. */
  wrapperClassName?: string;
}

/**
 * Floating sticky pill (zoom out / fit-to-width / zoom in + percentage)
 * used by every viewer that supports zoom (PDF, DOCX, XLSX/CSV) so the
 * three preview surfaces feel like a single family. Each call site passes
 * its own `ariaLabel` and `wrapperClassName`; visual styling is shared.
 */
const ZoomControlCluster: React.FC<ZoomControlClusterProps> = ({
  zoom,
  onZoomIn,
  onZoomOut,
  onFitWidth,
  ariaLabel,
  wrapperClassName,
}) => {
  const zoomPct = Math.round(zoom * 100);
  // Use a small epsilon so floating-point rounding (0.5000000003 etc.) never
  // mistakenly leaves a button enabled when it should be at the extreme.
  const atMaxZoom = zoom >= ZOOM_MAX - 1e-3;
  const atMinZoom = zoom <= ZOOM_MIN + 1e-3;
  const atFitWidth = Math.abs(zoom - 1) < 1e-3;

  const cls = wrapperClassName
    ? `doc-preview-zoom ${wrapperClassName}`
    : 'doc-preview-zoom';

  return (
    <div className={cls} role="group" aria-label={ariaLabel}>
      <button
        type="button"
        className="doc-preview-zoom-btn"
        onClick={onZoomOut}
        disabled={atMinZoom}
        aria-label="Depărtează"
        title="Depărtează"
      >
        <IconZoomOut />
      </button>
      <span
        className="doc-preview-zoom-label"
        aria-live="polite"
        aria-atomic="true"
      >
        {zoomPct}%
      </span>
      <button
        type="button"
        className="doc-preview-zoom-btn"
        onClick={onFitWidth}
        disabled={atFitWidth}
        aria-label="Adaptează la lățime"
        title="Adaptează la lățime"
      >
        <IconFitWidth />
      </button>
      <button
        type="button"
        className="doc-preview-zoom-btn"
        onClick={onZoomIn}
        disabled={atMaxZoom}
        aria-label="Apropie"
        title="Apropie"
      >
        <IconZoomIn />
      </button>
    </div>
  );
};

/**
 * Canvas-based PDF renderer used on every device. We stack each page as a
 * `<canvas>` sized to the container width and rendered at the device pixel
 * ratio for crisp output. Pages are rendered sequentially with a paint yield
 * between them so the UI stays responsive on large documents.
 *
 * A floating sticky control cluster (zoom in / fit-to-width / zoom out) sits
 * at the top-right of the viewer; changing the zoom triggers a re-render of
 * every page at the new scale. In-flight `RenderTask`s are cancelled before
 * a new zoom level kicks in so we never paint stale content over fresh.
 *
 * On any pdf.js failure (corrupt file, password-protected, worker init error)
 * we collapse into a fallback card that shows the filename and the only
 * "Deschide PDF" / "Descarcă" action buttons in the preview surface.
 */
const PdfCanvasViewer: React.FC<PdfCanvasViewerProps> = ({ url, blob, filename, maxHeight }) => {
  const hostRef = useRef<HTMLDivElement | null>(null);
  const renderTasksRef = useRef<Array<{ cancel?: () => void }>>([]);
  const pdfDocRef = useRef<any>(null);

  const [pdfDoc, setPdfDoc] = useState<any>(null);
  const [pageCount, setPageCount] = useState<number>(0);
  const [containerWidth, setContainerWidth] = useState<number>(0);
  const [zoom, setZoom] = useState<number>(1);
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  // ── PDF load: only depends on the underlying file. Splitting the load and
  // the per-page render lets us re-render on zoom changes without re-parsing
  // the document (which is the expensive part for large PDFs).
  useEffect(() => {
    let cancelled = false;
    const host = hostRef.current;
    setStatus('loading');
    setErrorMsg(null);
    setPdfDoc(null);
    setPageCount(0);
    setZoom(1);
    if (host) host.innerHTML = '';

    // Cancel any render tasks from the previous document immediately so they
    // don't paint into the cleared host once we get back to the event loop.
    for (const t of renderTasksRef.current) {
      try { t.cancel?.(); } catch { /* no-op */ }
    }
    renderTasksRef.current = [];

    (async () => {
      try {
        // Prefer the already-decrypted blob; only fall back to fetching the
        // blob: URL if the parent didn't pass a Blob (e.g. legacy callers).
        let arrayBuf: ArrayBuffer;
        if (blob) {
          arrayBuf = await blob.arrayBuffer();
        } else {
          const resp = await fetch(url);
          arrayBuf = await resp.arrayBuffer();
        }
        if (cancelled) return;

        const pdfjsLib = await getPdfJsLib();
        if (cancelled) return;

        // Use a fresh Uint8Array so pdf.js owns the buffer (it transfers
        // ownership to the worker and would otherwise detach our blob's data).
        const data = new Uint8Array(arrayBuf.slice(0));
        const loadingTask: any = (pdfjsLib as any).getDocument({ data });
        const pdf: any = await loadingTask.promise;
        if (cancelled) {
          try { pdf.destroy?.(); } catch { /* no-op */ }
          return;
        }
        // Destroy any previously-loaded document still hanging around (e.g.
        // the user paged to a different file while the prior load was queued).
        const prev = pdfDocRef.current;
        pdfDocRef.current = pdf;
        if (prev && prev !== pdf) {
          try { prev.destroy?.(); } catch { /* no-op */ }
        }
        setPdfDoc(pdf);
        setPageCount(pdf.numPages || 0);
      } catch (err: any) {
        if (cancelled) return;
        setErrorMsg(err?.message || 'Nu am putut afișa PDF-ul.');
        setStatus('error');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [url, blob]);

  // Destroy the loaded PDF on unmount so pdf.js releases its worker-side data.
  // Separated from the render effect so a zoom change doesn't tear down the
  // document; we only destroy when the viewer itself unmounts.
  useEffect(() => {
    return () => {
      for (const t of renderTasksRef.current) {
        try { t.cancel?.(); } catch { /* no-op */ }
      }
      renderTasksRef.current = [];
      const prev = pdfDocRef.current;
      pdfDocRef.current = null;
      if (prev) {
        try { prev.destroy?.(); } catch { /* no-op */ }
      }
    };
  }, []);

  // Track the host's CSS width so the fit-to-width baseline (zoom = 1.0)
  // adapts when the surrounding layout reflows (sidebar opens, viewport
  // resizes, etc.). Re-rendering happens in the effect below.
  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;
    const update = () => {
      const w = Math.max(280, host.clientWidth || 320);
      setContainerWidth((prev) => (prev === w ? prev : w));
    };
    update();
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(update);
    ro.observe(host);
    return () => ro.disconnect();
  }, []);

  // ── Per-page render: re-runs on every zoom / container-width / doc change.
  // Pages render sequentially with a rAF yield between them so the main
  // thread stays responsive when rasterising a large PDF at high zoom.
  useEffect(() => {
    const host = hostRef.current;
    if (!host || !pdfDoc || containerWidth === 0) return;

    let cancelled = false;
    // Cancel any in-flight render tasks from the previous zoom level so they
    // don't paint stale viewports into canvases we're about to replace.
    for (const t of renderTasksRef.current) {
      try { t.cancel?.(); } catch { /* no-op */ }
    }
    renderTasksRef.current = [];
    host.innerHTML = '';

    (async () => {
      try {
        // DPR is capped at 2× so we don't allocate huge backing stores on
        // high-density displays — combined with a 4× zoom ceiling this keeps
        // the worst-case canvas memory bounded.
        const dpr = Math.min(window.devicePixelRatio || 1, 2);
        const cssWidth = containerWidth;
        const totalPages = pdfDoc.numPages || 0;

        for (let pageNum = 1; pageNum <= totalPages; pageNum++) {
          if (cancelled) return;
          const page = await pdfDoc.getPage(pageNum);
          if (cancelled) return;
          const viewport1 = page.getViewport({ scale: 1 });
          // Effective CSS scale = fit-to-container × user zoom. At zoom = 1.0
          // this is the original fit-to-width behaviour; at zoom > 1 the
          // canvas exceeds the container and the pages wrapper scrolls.
          const fitScale = cssWidth / viewport1.width;
          const cssScale = fitScale * zoom;
          const viewport = page.getViewport({ scale: cssScale });

          const canvas = document.createElement('canvas');
          canvas.className = 'doc-preview-pdf-canvas';
          canvas.width = Math.floor(viewport.width * dpr);
          canvas.height = Math.floor(viewport.height * dpr);
          canvas.style.width = `${Math.floor(viewport.width)}px`;
          canvas.style.height = `${Math.floor(viewport.height)}px`;
          const ctx = canvas.getContext('2d');
          if (!ctx) continue;
          host.appendChild(canvas);

          const renderTask = page.render({
            canvasContext: ctx,
            viewport,
            transform: dpr !== 1 ? [dpr, 0, 0, dpr, 0, 0] : undefined,
          });
          renderTasksRef.current.push(renderTask);
          try {
            await renderTask.promise;
          } catch (e: any) {
            // pdf.js throws `RenderingCancelledException` when .cancel() runs.
            // That's an expected outcome on zoom change / unmount, so just
            // bail out without flipping the viewer into the error state.
            if (cancelled || e?.name === 'RenderingCancelledException') return;
            throw e;
          }
          if (cancelled) return;
          try { page.cleanup?.(); } catch { /* no-op */ }

          // Yield to the browser between pages so layout / scrolling stays
          // smooth on large documents (a multi-hundred-page PDF would
          // otherwise block the main thread for seconds at a stretch).
          if (pageNum < totalPages) {
            await new Promise<void>((resolve) => {
              if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
                window.requestAnimationFrame(() => resolve());
              } else {
                setTimeout(resolve, 0);
              }
            });
          }
        }

        if (!cancelled) setStatus('ready');
      } catch (err: any) {
        if (cancelled) return;
        setErrorMsg(err?.message || 'Nu am putut afișa PDF-ul.');
        setStatus('error');
      }
    })();

    return () => {
      cancelled = true;
      for (const t of renderTasksRef.current) {
        try { t.cancel?.(); } catch { /* no-op */ }
      }
      renderTasksRef.current = [];
    };
  }, [pdfDoc, zoom, containerWidth]);

  const openInNewTab = () => {
    try {
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch {
      // Popup blocked; the anchor fallback below still lets the user save.
    }
  };

  const handleZoomIn = () => setZoom((z) => clampZoom(z + ZOOM_STEP));
  const handleZoomOut = () => setZoom((z) => clampZoom(z - ZOOM_STEP));
  const handleFitWidth = () => setZoom(1);

  const zoomedIn = zoom > 1;

  return (
    <div className="doc-preview-pdf-viewer" style={{ maxHeight }}>
      {status !== 'error' && (
        <ZoomControlCluster
          zoom={zoom}
          onZoomIn={handleZoomIn}
          onZoomOut={handleZoomOut}
          onFitWidth={handleFitWidth}
          ariaLabel="Control mărire PDF"
          wrapperClassName="doc-preview-pdf-controls"
        />
      )}

      {status === 'loading' && (
        <div className="doc-preview-pdf-status">
          <div className="status-spinner" style={{ width: 16, height: 16 }} />
          <span>Se afișează documentul…</span>
        </div>
      )}

      {status === 'error' && (
        <div className="doc-preview-pdf-fallback" role="alert">
          <div className="doc-preview-pdf-fallback-title">Previzualizarea PDF a eșuat</div>
          {filename && (
            <div className="doc-preview-pdf-fallback-filename" title={filename}>
              {filename}
            </div>
          )}
          {errorMsg && (
            <div className="doc-preview-pdf-fallback-detail">{errorMsg}</div>
          )}
          <div className="doc-preview-pdf-fallback-actions">
            <button
              type="button"
              className="btn btn-primary"
              onClick={openInNewTab}
            >
              Deschide PDF
            </button>
            <a
              className="btn btn-secondary"
              href={url}
              download={filename || 'document.pdf'}
            >
              Descarcă
            </a>
          </div>
        </div>
      )}

      <div
        ref={hostRef}
        className={`doc-preview-pdf-pages${zoomedIn ? ' is-zoomed' : ''}`}
        aria-label={filename || 'Document PDF'}
        style={status === 'error' ? { display: 'none' } : undefined}
      />

      {status === 'ready' && pageCount > 1 && (
        <div className="doc-preview-pdf-footnote">{pageCount} pagini</div>
      )}
    </div>
  );
};

interface DocxViewerProps {
  blob: Blob | null | undefined;
  filename?: string | null;
  maxHeight: string;
}

/**
 * DOCX viewer with the same floating zoom cluster as the PDF surface.
 *
 * The percentage label and the +/− buttons drive a `displayedZoom` value
 * (range 0.5 → 4.0, step 0.25, identical to PDF). The actual transform
 * scale is `effectiveScale = naturalFitScale * displayedZoom`, where
 * `naturalFitScale = containerWidth / docxIntrinsicWidth`. That decoupling
 * is important because docx-preview emits each page as a `<section>` with
 * the original document's CSS page width baked in (e.g. ~793 px for A4),
 * which is typically NARROWER than the preview area — at "100%" the user
 * expects the document to fill the container width, not to render at its
 * intrinsic page size and look zoomed-out. Resetting `displayedZoom` to
 * `1.0` therefore corresponds to fit-to-width, exactly like PDF.
 *
 * `transform: scale()` doesn't affect layout — the parent scroll container
 * still sees the unscaled dimensions and won't grow scrollbars when zoomed
 * in. We compensate by sizing an outer placeholder wrapper to
 * `intrinsicW × effectiveScale` / `intrinsicH × effectiveScale`, which
 * gives the surrounding scroll container the correct scrollable area at
 * any zoom level. When the scaled content is narrower than the container
 * (displayed zoom < 100%), we centre the wrapper horizontally so the page
 * doesn't sit awkwardly against the left edge.
 *
 * The intrinsic page dimensions are measured once after `renderAsync`
 * resolves and refreshed via `ResizeObserver` to catch reflows from
 * late-loading images / font swaps. The container width is tracked via a
 * separate `ResizeObserver` so `naturalFitScale` stays accurate when the
 * surrounding layout changes (sidebar opens, viewport rotates, etc.).
 */
const DocxViewer: React.FC<DocxViewerProps> = ({ blob, filename, maxHeight }) => {
  // Outer scroll container — used to measure the viewport width that the
  // fit-to-width baseline targets.
  const viewerRef = useRef<HTMLDivElement | null>(null);
  // The element that gets `transform: scale()`. docx-preview renders its
  // `<section class="docx">` pages into a child of this node. We keep
  // `position: absolute; width: max-content` on it (see CSS) so its
  // intrinsic size is determined by the docx content, not by the
  // surrounding wrapper — which means our intrinsic measurement is stable
  // across zoom changes.
  const scaleRef = useRef<HTMLDivElement | null>(null);
  // Cached intrinsic dims (used inside the ResizeObserver callback to
  // skip no-op state updates without taking a render dependency on the
  // current state value).
  const intrinsicRef = useRef<{ w: number; h: number }>({ w: 0, h: 0 });

  // What the user sees in the percentage label / what the +/− buttons
  // mutate. Identical semantics + range to the PDF zoom (0.5–4.0, 100%
  // = fit-to-width).
  const [displayedZoom, setDisplayedZoom] = useState<number>(1);
  // Intrinsic (unscaled) bounds of the rendered docx content. Updated
  // via ResizeObserver so reflows from late-loading images / fonts are
  // picked up.
  const [intrinsicDims, setIntrinsicDims] = useState<{ w: number; h: number }>({ w: 0, h: 0 });
  // Inner content width of the outer scroll container (clientWidth minus
  // horizontal padding) — used as the fit-to-width target.
  const [containerWidth, setContainerWidth] = useState<number>(0);
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  // ── Load + render docx-preview. Runs only when the source blob changes;
  // zoom is purely a CSS transformation on the already-rendered tree, so
  // we never re-parse the document on zoom in/out.
  useEffect(() => {
    let cancelled = false;
    setStatus('loading');
    setErrorMsg(null);
    setDisplayedZoom(1);
    setIntrinsicDims({ w: 0, h: 0 });
    intrinsicRef.current = { w: 0, h: 0 };

    const target = scaleRef.current;
    if (target) target.innerHTML = '';

    if (!blob) {
      setErrorMsg('Blob indisponibil pentru previzualizare.');
      setStatus('error');
      return;
    }

    (async () => {
      try {
        const { renderAsync } = await import('docx-preview');
        if (cancelled) return;
        const tgt = scaleRef.current;
        if (!tgt) return;
        tgt.innerHTML = '';
        // Inner host the library renders into. Same shape as the original
        // implementation so the existing `.docx-preview-inner` /
        // `.docx-rendered` CSS rules continue to apply unchanged.
        const inner = document.createElement('div');
        inner.className = 'docx-preview-inner';
        tgt.appendChild(inner);
        await renderAsync(blob, inner, undefined, {
          inWrapper: true,
          ignoreWidth: false,
          ignoreHeight: true,
          ignoreFonts: false,
          breakPages: true,
          useBase64URL: true,
          className: 'docx-rendered',
        });
        if (cancelled) return;
        // Deterministic first measurement now that the library has
        // finished emitting its `<section>` page nodes — the RO below
        // takes over from here for any post-render reflows.
        const w = tgt.scrollWidth || tgt.offsetWidth || 0;
        const h = tgt.scrollHeight || tgt.offsetHeight || 0;
        intrinsicRef.current = { w, h };
        setIntrinsicDims({ w, h });
        setStatus('ready');
      } catch (err: any) {
        if (cancelled) return;
        setErrorMsg(err?.message || 'Nu am putut afișa documentul.');
        setStatus('error');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [blob]);

  // ── Track the outer container's content width. naturalFitScale is
  // derived from this on every render, so a side panel opening or a
  // tablet rotation re-fits the docx without any extra wiring.
  useEffect(() => {
    const viewer = viewerRef.current;
    if (!viewer) return;
    const measure = () => {
      // Strip the `.doc-preview-docx-viewer` horizontal padding so the
      // fit-to-width target matches the actual usable content area
      // (otherwise the docx would render slightly wider than the visible
      // region and trigger a spurious horizontal scroll at 100%).
      const cs = window.getComputedStyle(viewer);
      const padL = parseFloat(cs.paddingLeft) || 0;
      const padR = parseFloat(cs.paddingRight) || 0;
      const w = Math.max(0, (viewer.clientWidth || 0) - padL - padR);
      setContainerWidth((prev) => (prev === w ? prev : w));
    };
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(measure);
    ro.observe(viewer);
    return () => ro.disconnect();
  }, []);

  // ── Refresh intrinsic dims on post-render reflows (image loads, font
  // swaps, etc.). The deterministic measurement inside the load effect
  // covers the initial paint; this RO covers everything after.
  useEffect(() => {
    const target = scaleRef.current;
    if (!target) return;
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => {
      const w = target.scrollWidth || target.offsetWidth || 0;
      const h = target.scrollHeight || target.offsetHeight || 0;
      const prev = intrinsicRef.current;
      if (prev.w === w && prev.h === h) return;
      intrinsicRef.current = { w, h };
      setIntrinsicDims({ w, h });
    });
    ro.observe(target);
    return () => ro.disconnect();
  }, [status]);

  const handleZoomIn = () => setDisplayedZoom((z) => clampZoom(z + ZOOM_STEP));
  const handleZoomOut = () => setDisplayedZoom((z) => clampZoom(z - ZOOM_STEP));
  const handleFitWidth = () => setDisplayedZoom(1);

  // The "100%" label is fit-to-width — multiply the displayed zoom by the
  // ratio that makes the docx exactly fill the container's content width.
  // Falls back to identity when we don't yet have measurements (used only
  // during the brief loading window before status flips to 'ready').
  const haveDims = intrinsicDims.w > 0 && containerWidth > 0;
  const naturalFitScale = haveDims ? containerWidth / intrinsicDims.w : 1;
  const effectiveScale = naturalFitScale * displayedZoom;
  const ready = status === 'ready' && haveDims;

  const scaledWidth = intrinsicDims.w * effectiveScale;
  const scaledHeight = intrinsicDims.h * effectiveScale;
  // Centre the page horizontally only when the scaled content is narrower
  // than the container (i.e. displayed zoom < 100% on a wide viewport, or
  // a deliberately small docx). When the page is wider than the container
  // we want left alignment so horizontal scroll exposes the full content
  // from its left edge — same behaviour as the PDF viewer when zoomed in.
  // Use a small epsilon so values that are equal-up-to-floating-point
  // don't toggle alignment back and forth.
  const centerHorizontally = ready && scaledWidth + 0.5 < containerWidth;

  const wrapperStyle: React.CSSProperties | undefined = ready
    ? { width: `${scaledWidth}px`, height: `${scaledHeight}px` }
    : undefined;

  // Don't show the cluster until we have real measurements — otherwise
  // the percentage label would briefly read "100%" while the page is
  // visibly at intrinsic size, which is exactly the bug we're fixing.
  const showCluster = ready;

  return (
    <div ref={viewerRef} className="doc-preview-docx-viewer" style={{ maxHeight }}>
      {showCluster && (
        <ZoomControlCluster
          zoom={displayedZoom}
          onZoomIn={handleZoomIn}
          onZoomOut={handleZoomOut}
          onFitWidth={handleFitWidth}
          ariaLabel="Control mărire document"
          wrapperClassName="doc-preview-docx-controls"
        />
      )}

      <div className="doc-preview-docx-scroll">
        {status === 'loading' && (
          <div className="doc-preview-pdf-status">
            <div className="status-spinner" style={{ width: 16, height: 16 }} />
            <span>Se afișează documentul…</span>
          </div>
        )}

        {status === 'error' && (
          <div className="alert alert-error" role="alert">
            {errorMsg || 'Nu am putut afișa documentul.'}
          </div>
        )}

        <div
          className={`doc-preview-docx-scale-wrap${centerHorizontally ? ' is-centered' : ''}`}
          style={status === 'error' ? { display: 'none' } : wrapperStyle}
        >
          <div
            ref={scaleRef}
            className="doc-preview-docx-scale"
            aria-label={filename || 'Document Word'}
            style={{ transform: `scale(${effectiveScale})` }}
          />
        </div>
      </div>
    </div>
  );
};

interface XlsxSheetViewerProps {
  blob: Blob | null | undefined;
  filename?: string | null;
  /** When `true`, the source is a CSV (single sheet). XLSX may carry many
   *  sheets, which we stack vertically — sheet handling is unchanged from
   *  the original implementation. */
  isCsv: boolean;
  maxHeight: string;
}

/**
 * XLSX / CSV viewer with the same floating zoom cluster as the PDF and
 * DOCX surfaces.
 *
 * Tables benefit from `font-size` scaling rather than `transform: scale()`:
 * cells naturally reflow their widths to match the new text size, and the
 * surrounding scroll container's scrollbars stay accurate without any
 * compensation work. We expose a `--xlsx-zoom` CSS custom property on the
 * viewer wrapper; the existing `.doc-preview-sheet-wrap` rules are extended
 * (in CSS) to multiply font-size and cell padding by that variable.
 *
 * The sheet handling itself (one `<table>` per sheet for XLSX, single table
 * for CSV) is preserved verbatim from the previous implementation.
 */
const XlsxSheetViewer: React.FC<XlsxSheetViewerProps> = ({
  blob,
  filename,
  isCsv,
  maxHeight,
}) => {
  const hostRef = useRef<HTMLDivElement | null>(null);

  const [zoom, setZoom] = useState<number>(1);
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  // ── Parse the workbook + render each sheet as a `<table>`. SheetJS's
  // `sheet_to_html` returns ready-to-mount markup, so this stays imperative
  // (matches the legacy implementation we replaced). Zoom is a separate
  // concern that lives entirely in CSS, so we never re-parse on zoom.
  useEffect(() => {
    let cancelled = false;
    setStatus('loading');
    setErrorMsg(null);
    setZoom(1);

    const host = hostRef.current;
    if (host) host.innerHTML = '';

    if (!blob) {
      setErrorMsg('Blob indisponibil pentru previzualizare.');
      setStatus('error');
      return;
    }

    (async () => {
      try {
        const XLSX = await import('xlsx');
        if (cancelled) return;
        const ab = await blob.arrayBuffer();
        const wb = XLSX.read(ab, { type: 'array' });
        if (!wb.SheetNames.length) {
          setErrorMsg('Foaia de calcul nu conține niciun tab.');
          setStatus('error');
          return;
        }
        const tgt = hostRef.current;
        if (!tgt) return;
        tgt.innerHTML = '';
        // CSV → always exactly one sheet; XLSX → may have many. Render each
        // with a heading so multi-tab workbooks remain navigable. We skip
        // the heading on CSV since the synthetic sheet name ("Sheet1") is
        // not meaningful to the user.
        const showHeadings = !isCsv && wb.SheetNames.length > 1;
        for (const name of wb.SheetNames) {
          if (cancelled) return;
          if (showHeadings) {
            const heading = document.createElement('h4');
            heading.className = 'doc-preview-sheet-title';
            heading.textContent = name;
            tgt.appendChild(heading);
          }
          const wrap = document.createElement('div');
          wrap.className = 'doc-preview-sheet-wrap';
          // sheet_to_html returns a complete <table>…</table> string.
          wrap.innerHTML = XLSX.utils.sheet_to_html(wb.Sheets[name]);
          tgt.appendChild(wrap);
        }
        setStatus('ready');
      } catch (err: any) {
        if (cancelled) return;
        setErrorMsg(err?.message || 'Nu am putut afișa foaia de calcul.');
        setStatus('error');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [blob, isCsv]);

  const handleZoomIn = () => setZoom((z) => clampZoom(z + ZOOM_STEP));
  const handleZoomOut = () => setZoom((z) => clampZoom(z - ZOOM_STEP));
  const handleFitWidth = () => setZoom(1);

  // We pipe `zoom` through a CSS custom property on the viewer so the
  // existing `.doc-preview-sheet-wrap` rules can scale font-size + cell
  // padding via `calc()`. Cast through `Record<string, ...>` because
  // `React.CSSProperties` doesn't type custom properties.
  const viewerStyle: React.CSSProperties = {
    maxHeight,
    ...({ '--xlsx-zoom': `${zoom}` } as Record<string, string>),
  };

  return (
    <div className="doc-preview-xlsx-viewer" style={viewerStyle}>
      {status !== 'error' && (
        <ZoomControlCluster
          zoom={zoom}
          onZoomIn={handleZoomIn}
          onZoomOut={handleZoomOut}
          onFitWidth={handleFitWidth}
          ariaLabel="Control mărire foaie de calcul"
          wrapperClassName="doc-preview-xlsx-controls"
        />
      )}

      <div className="doc-preview-xlsx-scroll">
        {status === 'loading' && (
          <div className="doc-preview-pdf-status">
            <div className="status-spinner" style={{ width: 16, height: 16 }} />
            <span>Se afișează foaia de calcul…</span>
          </div>
        )}

        {status === 'error' && (
          <div className="alert alert-error" role="alert">
            {errorMsg || 'Nu am putut afișa foaia de calcul.'}
          </div>
        )}

        <div
          ref={hostRef}
          className="doc-preview-xlsx-host"
          aria-label={filename || (isCsv ? 'Fișier CSV' : 'Foaie de calcul')}
          style={status === 'error' ? { display: 'none' } : undefined}
        />
      </div>
    </div>
  );
};

export interface DocumentPreviewProps {
  /** A blob URL pointing at the file (created via URL.createObjectURL). */
  url: string;
  /** Optional in-memory Blob; required for DOCX/XLSX/CSV/TXT/RTF renderers
   *  that need to read the raw bytes (the image path only needs `url`). */
  blob?: Blob | null;
  /** Original filename — used for the image alt, fallback card label, and
   *  as a fallback for detecting the file type when `mime` is missing/octet-stream. */
  filename?: string | null;
  /** MIME type as reported by the browser / E2EE metadata blob. */
  mime?: string | null;
  /** Cap the preview height. */
  maxHeight?: string;
}

/**
 * Inline preview for the user's documents. Renders PDFs via a pdf.js canvas
 * pipeline (same on mobile and desktop), images via <img>, DOCX with
 * `docx-preview`, XLSX/CSV via SheetJS (`xlsx`), and TXT/RTF inline. PDF,
 * DOCX, and XLSX/CSV each get the same floating zoom cluster (− / fit / +)
 * via the shared `ZoomControlCluster` component, so the three previews
 * feel like one family. All non-trivial libs are lazy-imported on first
 * use so the initial bundle stays small for the common PDF/image case.
 */
const DocumentPreview: React.FC<DocumentPreviewProps> = ({
  url,
  blob,
  filename,
  mime,
  maxHeight = '80vh',
}) => {
  const kind = resolveKind(mime, filename);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // TXT / RTF are still rendered imperatively into `containerRef` (PDF,
  // DOCX, XLSX, CSV all live in their own components with private state
  // and zoom plumbing). Effect re-runs whenever the underlying blob
  // changes — i.e. the user opened a different document or re-decrypted
  // the same one.
  useEffect(() => {
    if (kind !== 'text' && kind !== 'rtf') return;
    if (!blob) {
      setLoadError('Blob indisponibil pentru previzualizare.');
      return;
    }
    let cancelled = false;
    setLoading(true);
    setLoadError(null);

    (async () => {
      const container = containerRef.current;
      if (!container) return;
      container.innerHTML = '';

      try {
        if (kind === 'text') {
          const text = await blob.text();
          if (cancelled) return;
          const pre = document.createElement('pre');
          pre.className = 'doc-preview-text';
          pre.textContent = text;
          container.appendChild(pre);
        } else if (kind === 'rtf') {
          // Minimal RTF → plaintext fallback: strip control words and groups.
          // Faithful RTF rendering would need a full parser; for medical docs
          // (typically prescriptions / consult notes) the plain text is what
          // matters and this is good enough for an at-a-glance preview.
          const raw = await blob.text();
          if (cancelled) return;
          const plain = raw
            .replace(/\\par[d]?/g, '\n')
            .replace(/\\'[0-9a-fA-F]{2}/g, '')
            .replace(/\\[a-zA-Z]+-?\d* ?/g, '')
            .replace(/[{}]/g, '')
            .replace(/\\\*/g, '')
            .trim();
          const pre = document.createElement('pre');
          pre.className = 'doc-preview-text';
          pre.textContent = plain;
          container.appendChild(pre);
        }
      } catch (err: any) {
        if (!cancelled) {
          setLoadError(err?.message || 'Eroare la afișarea documentului.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [kind, blob, url]);

  if (kind === 'pdf') {
    // Same pdf.js canvas pipeline for every device — desktop browsers can do
    // <iframe> blob: previews, but mobile (iOS Safari / WKWebView, Android
    // Chrome) cannot, and the user prefers a single consistent renderer.
    return (
      <PdfCanvasViewer url={url} blob={blob} filename={filename} maxHeight={maxHeight} />
    );
  }

  if (kind === 'image') {
    return (
      <img
        src={url}
        alt={filename || 'Document imagine'}
        style={{ maxWidth: '100%', height: 'auto', borderRadius: 'var(--radius-md)' }}
      />
    );
  }

  if (kind === 'docx') {
    return <DocxViewer blob={blob} filename={filename} maxHeight={maxHeight} />;
  }

  if (kind === 'xlsx' || kind === 'csv') {
    return (
      <XlsxSheetViewer
        blob={blob}
        filename={filename}
        isCsv={kind === 'csv'}
        maxHeight={maxHeight}
      />
    );
  }

  if (kind === 'unsupported') {
    return (
      <div className="empty-state" style={{ padding: 'var(--sp-8)' }}>
        <div className="empty-state-title">Previzualizare indisponibilă</div>
        <div className="empty-state-text">
          Tipul de fișier nu poate fi previzualizat în browser. Folosește butonul „Descarcă" pentru a-l salva.
        </div>
      </div>
    );
  }

  return (
    <div className="doc-preview-rich">
      {loading && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2)', color: 'var(--color-slate-500)', padding: 'var(--sp-3)' }}>
          <div className="status-spinner" style={{ width: 16, height: 16 }} />
          Se încarcă previzualizarea…
        </div>
      )}
      {loadError && (
        <div className="alert alert-error" style={{ marginBottom: 'var(--sp-3)' }}>
          {loadError}
        </div>
      )}
      <div
        ref={containerRef}
        className={`doc-preview-host doc-preview-host--${kind}`}
        style={{ maxHeight, overflow: 'auto' }}
      />
    </div>
  );
};

export default DocumentPreview;
